import { useState } from "react";
import { toast } from "sonner";
import { Lock, ShoppingBag, ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import { FROZEN_PRODUCTS, SQUARE_STORE_URL } from "@/lib/data";
import { joinWaitlist } from "@/lib/api";

function ProductCard({ product }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [variant, setVariant] = useState(product.variants?.[0] || "");
  const live = Boolean(SQUARE_STORE_URL);

  const onJoin = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await joinWaitlist(email, product.id);
      toast.success(`You're on the waitlist for ${product.name}.`);
      setEmail("");
    } catch {
      toast.error("Couldn't add you. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const buyHref = live
    ? `${SQUARE_STORE_URL}?product=${encodeURIComponent(product.id)}&variant=${encodeURIComponent(variant)}`
    : null;

  return (
    <Reveal className="group bg-[#1A1A1A] border border-[#F5ECD7]/5 hover:border-[#C99C3A]/40 transition-colors rounded-2xl overflow-hidden">
      <div className="relative aspect-square overflow-hidden">
        <img src={product.image} alt={product.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        {!live && (
          <div className="absolute inset-0 bg-black/45 backdrop-blur-[2px] flex items-center justify-center">
            <div className="text-center">
              <Lock size={24} className="text-[#C99C3A] mx-auto mb-2" />
              <p className="text-xs uppercase tracking-[0.3em] text-[#C99C3A]">Launching Soon</p>
            </div>
          </div>
        )}
        <div className="absolute top-4 right-4 bg-[#121212]/85 backdrop-blur-md text-[#C99C3A] font-display text-lg px-3 py-1 rounded-full">
          {product.price}
        </div>
      </div>
      <div className="p-6 md:p-7">
        <h3 className="font-display text-2xl tracking-tight text-[#F5ECD7]">{product.name}</h3>
        <p className="mt-2 text-[#F5ECD7]/60 text-sm leading-relaxed">{product.desc}</p>

        {product.variants && live && (
          <div className="mt-4 flex flex-wrap gap-2">
            {product.variants.map((v) => (
              <button
                key={v}
                onClick={() => setVariant(v)}
                data-testid={`variant-${product.id}-${v.toLowerCase().replace(/\s+/g, "-")}`}
                className={`text-[11px] uppercase tracking-[0.2em] px-3 py-1.5 border transition-colors ${
                  variant === v
                    ? "border-[#C99C3A] bg-[#C99C3A] text-[#121212]"
                    : "border-[#F5ECD7]/15 text-[#F5ECD7]/65 hover:border-[#C99C3A]/60"
                }`}
              >
                {v}
              </button>
            ))}
          </div>
        )}

        {live ? (
          <a
            href={buyHref}
            target="_blank"
            rel="noreferrer"
            data-testid={`buy-${product.id}`}
            className="mt-5 btn-gold w-full justify-center"
          >
            <ShoppingBag size={14} /> Buy Now <ArrowUpRight size={14} />
          </a>
        ) : (
          <form onSubmit={onJoin} className="mt-5 flex items-end gap-3">
            <input
              type="email"
              required
              placeholder="Notify me at..."
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid={`waitlist-email-${product.id}`}
              className="input-line flex-1 text-sm"
            />
            <button data-testid={`waitlist-submit-${product.id}`} type="submit" disabled={loading} className="btn-gold !py-2.5 !px-4 !text-[10px]">
              {loading ? "..." : "Notify Me"}
            </button>
          </form>
        )}
      </div>
    </Reveal>
  );
}

export default function Shop() {
  const live = Boolean(SQUARE_STORE_URL);
  return (
    <div data-testid="shop-page" className="bg-[#121212]">
      <section className="pt-20 md:pt-28 pb-12 border-b border-[#F5ECD7]/10">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="flex items-center gap-3 mb-5">
            <span className="text-xs uppercase tracking-[0.3em] text-[#C99C3A]">Frozen Taco Shop</span>
            {!live && (
              <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-1 border border-[#9B2226] text-[#9B2226]">
                Launching Soon
              </span>
            )}
          </div>
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.98] text-[#F5ECD7]">
            The truck,
            <br />
            <span className="font-serif-italic font-normal text-[#C99C3A]">in your freezer</span>.
          </h1>
          <p className="mt-6 max-w-2xl text-[#F5ECD7]/65 text-lg">
            12-packs · $49 each · ground beef, chicken, carnitas, jerk chicken
            · corn or flour tortilla · toppings sold separately. Secure
            checkout powered by Square.
          </p>
          {live && (
            <a
              href={SQUARE_STORE_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="shop-full-store"
              className="mt-8 inline-flex btn-gold"
            >
              <ShoppingBag size={16} /> Visit Full Shop
            </a>
          )}
        </div>
      </section>

      <section className="section-pad">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {FROZEN_PRODUCTS.map((p) => (<ProductCard key={p.id} product={p} />))}
        </div>

        <Reveal className="mt-20 md:mt-28 text-center max-w-2xl mx-auto">
          <h3 className="font-display text-3xl md:text-4xl tracking-tighter text-[#F5ECD7]">
            Subscription packs.{" "}
            <span className="font-serif-italic font-normal text-[#C99C3A]">Coming with launch.</span>
          </h3>
          <p className="mt-4 text-[#F5ECD7]/65">
            Save when you subscribe to monthly variety boxes. Secure checkout via Square.
          </p>
        </Reveal>
      </section>
    </div>
  );
}
